import React, { useState } from "react";
import { Box, Image, useToast } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import useViewPortWidth from "src/utils/use-viewport-width";
import { useBioFlowHandoff } from "src/hooks/bioflow/useBioFlowHandoff";
import { useBioFlowAccount } from "src/hooks/bioflow/useBioFlowAccount";

const BIOFLOW_ICON = "/sal-bioflow-icon.png";
const BIOFLOW_LOGO = "/sal-bioflow-logo.svg";

const BioFlowButton: React.FC = () => {
  const [isHovered, setIsHovered] = useState(false);
  const { viewPortWidth } = useViewPortWidth();
  const { formatMessage } = useIntl();
  const toast = useToast();
  const { startHandoff, isStarting } = useBioFlowHandoff();
  const {
    loading: accountLoading,
    onboardingComplete,
    refresh: refreshAccount,
  } = useBioFlowAccount();

  const isLoading = accountLoading;
  const enableHoverEffect = viewPortWidth >= 768;

  const handleClick = async () => {
    if (isStarting || isLoading) return;
    const popup = window.open("", "_blank");
    try {
      await startHandoff(popup);
      await refreshAccount();
    } catch {
      if (popup && !popup.closed) {
        popup.close();
      }
      toast({
        title: formatMessage({
          id: "bioflow.error.handoffFailed",
          defaultMessage: "Could not open SALBioFlow",
        }),
        description: formatMessage({ id: "wentWrong" }),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getHoverText = () => {
    if (onboardingComplete) {
      return formatMessage({
        id: "bioflow.button.access",
        defaultMessage: "Open SALBioFlow",
      });
    }
    return formatMessage({
      id: "bioflow.button.getStarted",
      defaultMessage: "Get Started",
    });
  };

  return (
    <Box
      position="relative"
      width="40px"
      height="40px"
      onMouseEnter={() => enableHoverEffect && setIsHovered(true)}
      onMouseLeave={() => enableHoverEffect && setIsHovered(false)}
      transform="translateY(10%)"
      overflow="visible"
      cursor={isStarting || isLoading ? "wait" : "pointer"}
      onClick={handleClick}
      pointerEvents={isStarting || isLoading ? "none" : "auto"}
      aria-label={getHoverText()}
    >
      <Box
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          display: "flex",
          alignItems: "center",
          width: isHovered ? "180px" : "40px",
          height: "40px",
          overflow: "hidden",
          transition: "width 0.5s ease-in-out",
        }}
      >
        <Image
          src={BIOFLOW_ICON}
          alt="SALBioFlow"
          width="40px"
          height="40px"
          objectFit="contain"
          flexShrink={0}
          fallbackSrc="/final-logo-SAL-152.png"
        />
        <Box
          display="flex"
          alignItems="center"
          transform={isHovered ? "translateX(8px)" : "translateX(-10px)"}
          opacity={isHovered ? 1 : 0}
          transition="all 0.5s ease-in-out"
          whiteSpace="nowrap"
          pointerEvents="none"
        >
          <Image
            src={BIOFLOW_LOGO}
            alt="SALBioFlow logo"
            width="125px"
            maxHeight="35px"
            objectFit="contain"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default BioFlowButton;
